import {SubscriptionManager} from './subscriptions.js';

/** @type {Map<string, Collection>} */
const collections = new Map();

/** @typedef {(item:*) => boolean} predicate */

/** @typedef {Collection[]} CollectionReference */

/**
 * Creates a new collection with a given name and items. Throws an error if the collection already exists.
 * @param {string} collectionName A unique name for the new collection.
 * @param {function(any): string} idFunction A function to generate unique IDs from items in this collection.
 * @param  {...any} [items] One or more items to add to the new collection.
 * @return {Collection} The newly-created collection.
 */
function createCollection(collectionName, idFunction, ...items) {
  // Perform validations to make sure the collection can be created
  if (collections.has(collectionName)) {
    throw new Error(`Unable to create collection '${collectionName}'; already exists.`);
  }
  const collection = new Collection(collectionName, idFunction);
  collections.set(collectionName, collection);
  if (items.length) {
    collection.addItems(...items);
  }
  return collection;
}

/**
 * Destroys an existing collection with a given name, allowing the name to be re-used.
 * Ensures a clean slate when working with sequential unit tests. Throws an error if the collection does not exist.
 * @param {string} collectionName The name of the collection to destroy.
 */
function destroyCollection(collectionName) {
  // Make sure the collection exists before trying to destroy it.
  if (!collections.has(collectionName)) {
    throw new Error(`Unable to destroy collection '${collectionName}'; does not exist.`);
  }
  collections.delete(collectionName);
}

/**
 * Retrieves a reference to a collection if it has already been created.
 * @param {string} collectionName The name of the collection.
 * @return {Collection} The retrieved collection.
 */
function getCollection(collectionName) {
  return collections.get(collectionName);
}

/**
 * An object informing subscribers of changes whenever a filtered collection updates.
 * @typedef {object} CollectionUpdate
 * @property {Array} included The items included by filters.
 * @property {Array} added The items not included in the previous update, but included in this one.
 * @property {Array} removed The items included in the previous update, but not in this one.
 *    Removed items still exist in the collection's source; they are merely filtered out (hidden).
 * @property {Array} updated The items included in both the previous update and this one, whose data changed.
 * @property {string[]} deleted The ids of items dropped from the collection's source, whether or
 *    not they were filtered in. Stronger than removal: consumers should destroy resources keyed by
 *    these ids (e.g. map registry entries), not just hide them.
 */

/** Class representing a filterable collection and associated filters. */
class Collection {
  /**
   * Creates a new collection.
   * @param {string} name A unique name identifying this collection.
   * @param {Function} idFunction A function to generate unique IDs from items in this collection.
   */
  constructor(name, idFunction) {
    if (!(idFunction instanceof Function)) {
      throw new TypeError('Invalid argument: idFunction must be a function.');
    }
    /** @type {string} @readonly */
    this.name = name;
    /** @type {(item:*) => string} @readonly */
    this.idFunction = idFunction;
    /** @type {Map<string, unknown>} @private */
    this.source = new Map();
    /** @type {Map<string, Filter>} @private */
    this.filters = new Map();
    /** @type {Map<string, *>} @private */
    this.filtered = new Map();
    /**
     * While set, decides `filtered` alone. The named filters stay registered and current, so clearing the override
     * restores exactly what they would show.
     * @type {Filter|null} @private
     */
    this.override = null;
    /** @type {SubscriptionManager} @private */
    this.subscriptions = new SubscriptionManager();
  }

  /**
   * Subscribes a new callback receive updates from this collection.
   * The following subscription event types are used by Collection:
   *    add: At least one item was added to the filtered results, and no items were removed.
   *    remove: At least one item was removed from the filtered results, and no items were added.
   *    update: At least one item was added and at least one item was removed from the filtered results.
   * @param {string|null} type The type of updates to be received.
   * @param {Function} callback A callback function to be called with updates.
   * @param {*} [options]
   */
  subscribe(type, callback, options) {
    return this.subscriptions.addSubscription(type, callback, options);
  }

  /**
   * Retrieves an array of items in this collection's source (unfiltered).
   * @return {object[]} An array of this collection's source items.
   */
  getSource() {
    const result = [];
    this.source.forEach((item) => result.push(item));
    return result;
  }

  /**
   * Determines whether the source contains an item with the given id.
   * @param {string} id The id to check for.
   * @return {boolean} `true` if the source contains an item with the given id, `false` otherwise.
   */
  sourceHasId(id) {
    return this.source.has(id);
  }

  /**
   * Retrieves a single item from the collection's unfiltered source by id.
   * @param {string} id The id of the item to retrieve.
   * @return {* | undefined} The retrieved item, or `undefined` if no item found.
   */
  getItem(id) {
    return this.source.get(id);
  }

  /**
   * Retrieves the items in this collection that are currently filtered in.
   * @param {boolean} [asMap] If `true`, return results in a `Map`. Otherwise, return them in an array.
   */
  getFiltered(asMap = false) {
    return asMap ? this.filtered : mapToArray(this.filtered);
  }

  /**
   * Retrieves the ids of items in this collection that are currently filtered in.
   * @return {Set<string>} The ids of items included by this collection's filters.
   */
  getFilteredIds() {
    const result = new Set();
    for (const id of this.filtered.keys()) {
      result.add(id);
    }
    return result;
  }

  /**
   * Adds a new filter to this collection, or updates an existing one, and applies it to all items.
   * An update will be published to subscribers if any items are freshly excluded by the filter.
   * @param {string} name A string to identify this filter
   * @param {predicate} predicate A predicate used by this filter to narrow down the collection.
   */
  addFilter(name, predicate) {
    if (this.filters.has(name)) { // This is an existing filter
      const filter = this.filters.get(name);
      filter.update(predicate);
      this.recalcFilter(name); // Delegate to recalcFilter
    } else { // This is a new filter
      const filter = new Filter(name, predicate);
      this.filters.set(name, filter);
      if (this.override) return;
      // Adding a new filter can only narrow down the already-filtered items.
      const {included, excluded} = filter.apply(this.filtered);
      this.filtered = included;
      // Change excluded to an array to be consistent with the existing filter branch.
      const removed = mapToArray(excluded);
      this.updateSubscribers(null, removed, null);
    }
  }

  /**
   * Removes a filter from this collection and recalculates filters accordingly.
   * Updates subscribers to this collection if this results in any new items being included.
   * @param {string} name The name of the filter to remove.
   */
  removeFilter(name) {
    if (!this.filters.has(name)) return; // No effect if called for a nonexistent filter
    const filter = this.filters.get(name);
    this.filters.delete(name);
    if (this.override) return;
    // Removing a filter may include new items in the filtered result and it will never remove items.
    // Only those items that were previously excluded by the filter are candidates to be freshly included.
    const {excluded} = filter.apply(this.source);
    // If an item that was excluded by this filter passes all of the others, add it.
    const {included} = this.applyFilters(excluded);
    included.forEach((item, id) => this.filtered.set(id, item));
    // Convert to array for output.
    const added = mapToArray(included);
    this.updateSubscribers(added, null, null);
  }

  /**
   * Recalculates a given filter and issues an update to subscribers if any items are freshly included or excluded.
   * This method should be called by the filter's owner when anything happens that would cause the filter's predicate
   * to produce different results from before.
   * @param {string} name The name of the filter to recalculate.
   */
  recalcFilter(name) {
    if (!this.filters.get(name)) {
      console.warn(`An attempt was made to recalculate filter '${name}', which does not exist.`);
      return;
    }
    if (this.override) return;
    // All filters must be recalculated because recalcing can cause items to pass this filter while failing others.
    this.refilter();
  }

  /**
   * Overrides this collection's filters: only items passing `predicate` are filtered in until `clearOverride`. Setting
   * it again replaces the previous override. Publishes the resulting changes to subscribers.
   * @param {predicate} predicate
   */
  setOverride(predicate) {
    this.override = new Filter('override', predicate);
    this.refilter();
  }

  /**
   * Hands filtering back to the named filters, publishing the resulting changes to subscribers.
   */
  clearOverride() {
    if (!this.override) return;
    this.override = null;
    this.refilter();
  }

  /**
   * Recalculates the filtered items from the source and publishes whatever was freshly included or excluded.
   * @private
   */
  refilter() {
    const added = [];
    const removed = [];
    const {included, excluded} = this.applyFilters(this.source);
    // Remove any items that were freshly filtered out.
    this.filtered.forEach((item, id) => {
      if (excluded.has(id)) {
        removed.push(item);
      }
    });
    // Include any items that were freshly filtered in.
    included.forEach((item, id) => {
      // Each such item must be tested against all other filters.
      if (!this.filtered.has(id)) {
        added.push(item);
      }
    });
    // Make the requisite changes to the filtered collection.
    removed.forEach((item) => this.filtered.delete(this.idFunction(item)));
    added.forEach((item) => this.filtered.set(this.idFunction(item), item));
    this.updateSubscribers(added, removed, null);
  }

  /**
   * Applies this collection's filters to the passed-in map.
   * @private
   * @param {Map} items The map of items to be filtered.
   * @return {FilterResult} A results object indicating which items were included and excluded by the filter.
   */
  applyFilters(items) {
    if (this.override) return this.override.apply(items);
    const result = {excluded: new Map()};
    /** @type {Array|Map} */
    let acc = items;
    this.filters.forEach((filter) => {
      const {included, excluded} = filter.apply(acc);
      acc = included;
      excluded.forEach((item, id) => result.excluded.set(id, item));
    });
    // The items left in the accumulator are those included by the filters.
    result.included = acc;
    return result;
  }

  /**
   * Add one or more items to this collection and apply its filters.
   * If any of the items already exist, they will be updated/replaced instead of skipped.
   * An update will be published to subscribers if this change results in items being newly filtered in or out.
   * @param  {...any} items One or more items to add to the collection.
   */
  addItems(...items) {
    const {added, removed, updated} = this.upsertItems(items);
    this.updateSubscribers(added, removed, updated);
  }

  /**
   * Replaces this collection's entire source: items absent from the new set are deleted, the rest
   * upserted, in a single published update. For data sources that return a complete statement of
   * what exists — deletions are detectable only by absence.
   * @param  {...any} items The complete new set of items for this collection.
   */
  replaceItems(...items) {
    const newIds = new Set();
    items.forEach((item) => {
      const id = this.idFunction(item);
      if (id != null) {
        newIds.add(id);
      }
    });
    const deleted = [];
    const removed = [];
    this.source.forEach((item, id) => {
      if (newIds.has(id)) return;
      deleted.push(id);
      if (this.filtered.delete(id)) {
        removed.push(item);
      }
    });
    deleted.forEach((id) => this.source.delete(id));
    const upserted = this.upsertItems(items);
    this.updateSubscribers(upserted.added, removed.concat(upserted.removed), upserted.updated, deleted);
  }

  /**
   * Upserts items into the source and filtered maps without publishing.
   * @private
   * @param {any[]} items
   * @return {{added: Array, removed: Array, updated: Array}} The resulting filtered-set changes.
   */
  upsertItems(items) {
    // We must determine which items are actually new and which are being updated.
    const existingItems = new Map();
    const newItems = new Map();
    const badItems = [];
    items.forEach((item) => {
      const id = this.idFunction(item);
      if (id == null) {
        badItems.push(item);
        return;
      }
      (this.source.has(id) ? existingItems : newItems).set(id, item);
      this.source.set(id, item);
    });
    if (badItems.length) {
      console.error(`Found ${badItems.length} items with invalid IDs.`, badItems);
    }
    const added = [];
    const removed = [];
    const updated = [];
    // Handle updated items
    if (existingItems.size) {
      const {included, excluded} = this.applyFilters(existingItems);
      // For each item that passes, if it wasn't already filtered in, include it and consider it added.
      // If it was already filtered in, consider it updated.
      included.forEach((item, id) => {
        if (!this.filtered.has(id)) {
          added.push(item);
        } else {
          updated.push(item);
        }
        this.filtered.set(id, item);
      });
      // For each item that fails, if it was already filtered in, exclude it and consider it removed.
      excluded.forEach((item, id) => {
        if (this.filtered.has(id)) {
          removed.push(item);
          this.filtered.delete(id);
        }
      });
    }
    // Handle new items
    // Adding new items will not affect filtration of any other items.
    if (newItems.size) {
      this.applyFilters(newItems).included.forEach((item, id) => {
        added.push(item);
        this.filtered.set(id, item);
      });
    }
    return {added, removed, updated};
  }

  /**
   * Removes items from this collection's source, publishing `deleted` even for items that were
   * filtered out at the time.
   * @param  {...any} items One or more items to remove. Only the id is used, so `{id}` stubs work.
   */
  removeItems(...items) {
    const removed = [];
    const deleted = [];
    items.forEach((item) => {
      const id = this.idFunction(item);
      if (!this.source.delete(id)) return;
      deleted.push(id);
      if (this.filtered.delete(id)) {
        removed.push(item);
      }
    });
    this.updateSubscribers(null, removed, null, deleted);
  }

  /**
   * Publishes an update to subscribers if any items were just added, removed, updated, or deleted.
   * @private
   * @param {Array} [added] The freshly-added items. May be omitted if nothing was added.
   * @param {Array} [removed] The freshly-removed items. May be omitted if nothing was removed.
   * @param {Array} [updated] The freshly-updated items. May be omitted if nothing was updated.
   * @param {string[]} [deleted] The ids of items dropped from the source. May be omitted if nothing was deleted.
   */
  updateSubscribers(added, removed, updated, deleted) {
    const didAdd = added instanceof Array && added.length;
    const didRemove = removed instanceof Array && removed.length;
    const didUpdate = updated instanceof Array && updated.length;
    const didDelete = deleted instanceof Array && deleted.length;
    if (!didAdd && !didRemove && !didUpdate && !didDelete) return;
    // Most of the time the type will be update
    // Occasionally, we only add or remove items
    let type = 'update';
    if (didAdd && !didRemove && !didUpdate && !didDelete) {
      type = 'add';
    } else if (!didAdd && !didUpdate && (didRemove || didDelete)) {
      type = 'remove';
    }
    // Early return if no subscribers care about this event type.
    if (!this.subscriptions.hasSubscriptions(type)) return;
    const included = mapToArray(this.filtered);
    /** @type {CollectionUpdate} */
    const update = {
      included,
      added: added ?? [],
      removed: removed ?? [],
      updated: updated ?? [],
      deleted: deleted ?? [],
    };
    this.subscriptions.publish(type, update);
  }
}

// Helper functions
/**
 * Creates an array representation of a map.
 * @param {Map} map The map.
 * @return {Array} An array representing the map.
 */
function mapToArray(map) {
  const result = [];
  map.forEach((item) => result.push(item));
  return result;
}

/**
 * @typedef {object} FilterResult
 * @property {Map} included
 * @property {Map} excluded
 */

/** Class representing a predicate to be applied to items in a `Collection`. */
class Filter {
  constructor(name, predicate) {
    /** @type {string} @private */
    this.name = name;
    /** @type {predicate} @private */
    this.predicate = predicate;
  }

  /**
   * Applies this filter to an iterable collection of items and returns results.
   * @param {Array|Map} items An iterable collection of items to filter.
   * @return {FilterResult} A results object indicating which items were included and excluded by the filter.
   */
  apply(items) {
    const included = new Map();
    const excluded = new Map();
    items.forEach((item, id) => (this.predicate(item) ? included : excluded).set(id, item));
    return {included, excluded};
  }

  /**
   * Updates the predicate associated with this filter.
   * It is the caller's responsibility to also re-apply this filter as needed by calling `apply`.
   * @param {predicate} predicate The new predicate.
   */
  update(predicate) {
    this.predicate = predicate;
  }
}

export {createCollection, destroyCollection, getCollection, Collection};
